'use client';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useCurrentEditor, Editor } from '@tiptap/react';

import useUpdateNookMutation from '@/hooks/mutations/useUpdateNookMutation';
import useCheckUpdatesQuery from '@/hooks/queries/useCheckUpdatesQuery';
import useFetchNookQuery from '@/hooks/queries/useFetchNookQuery';
import useDebouncedValue from '@/hooks/debounces/useDebouncedValue';
import { parseError } from '@/lib/errorHandlers';

type SaveStatus = {
  error: boolean;
  message: string;
};

export function useNookSync(nookId: string, initialVersion: number = 1) {
  const { editor } = useCurrentEditor();
  const saveContentRef = useRef<() => void>(null);
  const [editorContent, setEditorContent] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>({
    error: false,
    message: '',
  });
  const [version, setVersion] = useState(initialVersion);

  const debouncedContent = useDebouncedValue({
    value: editorContent,
    delay: 2000,
  });

  const { mutate, isPending: isSaving } = useUpdateNookMutation();

  const { data: updateCheck, isSuccess: updateCheckSuccess } =
    useCheckUpdatesQuery(nookId, version);

  const { refetch: refetchContent } = useFetchNookQuery(nookId);

  const saveContent = useCallback(() => {
    if (!editor) return; // Defensive check
    const previousContent = editor.getJSON();
    const payloadContent = debouncedContent || '';
    mutate(
      { nookId, content: payloadContent, version },
      {
        onSuccess: data => {
          setVersion(data.version);
          setSaveStatus({
            error: false,
            message: `Last saved: ${new Date().toLocaleTimeString()}.`,
          });
        },
        onError: error => {
          editor.commands.setContent(previousContent, false);
          const { message } = parseError(error);
          setSaveStatus({
            error: true,
            message,
          });
        },
      }
    );
  }, [nookId, debouncedContent, version, editor, mutate]);
  saveContentRef.current = saveContent;

  // Setup editor to listen for changes
  useEffect(() => {
    if (!editor) return;

    const handleUpdate = ({ editor: editorInstance }: { editor: Editor }) => {
      const content = editorInstance.getJSON();
      setEditorContent(JSON.stringify(content));
    };

    editor.on('update', handleUpdate);

    return () => {
      editor.off('update', handleUpdate);
    };
  }, [editor]);

  useEffect(() => {
    if (!debouncedContent) return;
    saveContentRef.current?.();
  }, [debouncedContent]);

  // Refetch content when updateCheckSuccess changes
  const hasUpdates = updateCheck?.hasUpdates;
  useEffect(() => {
    if (!updateCheckSuccess || !hasUpdates) return;
    if (!editor) return;
    if (isSaving) return;

    refetchContent().then(result => {
      if (!result.data) return;

      try {
        const serverContent = JSON.parse(result.data.content);
        editor.commands.setContent(serverContent, false);
        setVersion(result.data.version);
        setSaveStatus({
          error: false,
          message: `Content was updated from the server at: ${new Date().toLocaleTimeString()}.`,
        });
      } catch (error) {
        const { message } = parseError(error);
        setSaveStatus({
          error: true,
          message,
        });
      }
    });
  }, [updateCheckSuccess, hasUpdates, editor, refetchContent, isSaving]);

  //  TODO: Add a feature to prompt users if they have not saved their content.

  return { isSaving, saveStatus, version };
}
